import type { DocSourceStatement, EvidenceAttribution, EvidenceSection, SedSectionId } from '../../types/siteEvidence';
import type { SiteDocument } from '../../types/siteRag';

type SourceResult = { data: Record<string, unknown>; attribution?: EvidenceAttribution; excerptPreview?: string; pageNumber?: number; sourceOffset?: { start: number; end: number } };

/** Explicit source scope governs adoption even when model extraction succeeded. */
export function documentNonAdoptionReason(section: SedSectionId, result: Omit<SourceResult, 'data'>): DocSourceStatement['reason'] | undefined {
  if (result.attribution?.appliesToProject === 'other-project') return 'other-project';
  // A customer's load request is useful design context, but cannot fill utility
  // availability/capacity fields. Use the quoted passage, not unrelated prose.
  const quote = result.excerptPreview ?? '';
  if (section === 'grid' && /\b(?:request(?:ed|ing)?|planned|proposed|desired|seeking)\b/i.test(quote)
      && /\b(?:load|demand|MW|megawatts?)\b/i.test(quote)) return 'requested-load';
  if (section === 'grid' && (
    /\bfuture\s+expansion\s+(?:needs?|demand|requirements?)\b/i.test(quote)
    || /\bcustomer\s+(?:will\s+)?(?:construct|build|own)\b/i.test(quote)
    || /\bcustomer[- ]owned\s+(?:electrical\s+)?(?:facilities|substations?|transformers?)\b/i.test(quote)
  ) && !/\b(?:utility|grid|supplier)\b[^.!?\n]{0,80}\b(?:confirms?|commits?|offers?|available\s+capacity|can\s+(?:serve|supply|deliver))\b/i.test(quote)) {
    return 'requested-load';
  }
  return undefined;
}

/** Preserve the unadopted candidate and its exact source without claiming a gap was filled. */
export function retainDocumentSourceStatement(slot: EvidenceSection<unknown>, doc: Pick<SiteDocument, 'id' | 'fileName'>, result: SourceResult, reason: DocSourceStatement['reason']): void {
  slot.docSourceStatements = [
    ...(slot.docSourceStatements ?? []).filter(statement => statement.docId !== doc.id || statement.reason === 'unverified-site-connection'),
    { docId: doc.id, docName: doc.fileName, data: result.data, reason,
      ...(result.attribution ? { attribution: result.attribution } : {}),
      ...(result.excerptPreview ? { excerptPreview: result.excerptPreview } : {}),
      ...(result.pageNumber !== undefined ? { pageNumber: result.pageNumber } : {}),
      ...(result.sourceOffset ? { sourceOffset: result.sourceOffset } : {}),
    },
  ];
}
